import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@material-ui/core";
import {
  DeletePaymentPlan,
  GetAllPaymentPlans,
} from "../../../../../../Services/PaymentPlanServices";
import {
  BasicTable,
  TableSkeleton,
  DeleteConfirmationDialog,
  TableEditDeleteActions,
  CustomPagination,
  CustomButton,
} from "../../../../../../Components";
import { FilterIcon } from "../../../../../../assets/icons";
import { formatPaymentPlansForTableFields } from "../../../../PaymentPlans/models";
import { PaymentPlanTableHeaderData } from "./PaymentPlanTableHeaderData";
import { useSelectedTheme, useTranslate } from "../../../../../../Hooks";
import { useVerticalNav } from "../../../../../../Contexts/VerticalNavContext";
import PaymentPlanDialog from "./UI/PaymentPlanDialog/PaymentPlanDialog";

// styles
import useStyles from "./styles";

const NewPaymentPlansView = forwardRef(({ propertyId, children, onDelete }, ref) => {
  const styles = useStyles();
  const initialFilterWithOperator = {
    downPayment: { operator: null, value: "" },
    handoverPayment: { operator: null, value: "" },
  };

  const initialFilter = {
    developerName: null,
    paymentPlanName: null,
    propertyPlanStatus: null,
    isForSpecificProperty: !!propertyId,
  };

  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();
  const { isDarkMode } = useSelector((state) => state.theme);
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [activeItem, setActiveItem] = useState({});
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);
  const [IsOpenAddPaymentPlanMenu, setIsOpenAddPaymentPlanMenu] =
    useState(false);
  const [paymentPlansData, setPaymentPlansData] = useState({
    result: [],
    totalCount: 0,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 25,
  });
  const [InputValue, setInputValue] = useState(pagination.pageIndex);
  const [filterWithOperator, setFilterWithOperator] = useState(
    initialFilterWithOperator
  );
  const [filter, setFilter] = useState(initialFilter);

  const getAllPaymentPlans = useCallback(async () => {
    setIsLoading(true);
    const advanceSearch = {};
    if (filterWithOperator.downPayment.value) {
      advanceSearch.downPayment = filterWithOperator.downPayment.value / 100;
      advanceSearch.searchType = filterWithOperator.downPayment.operator;
    }
    if (filterWithOperator.handoverPayment.value) {
      advanceSearch.handoverInstallment =
        filterWithOperator.handoverPayment.value / 100;
      advanceSearch.searchType = filterWithOperator.handoverPayment.operator;
    }
    const body = {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      propertyId: Number(propertyId),
      ...filter,
      ...advanceSearch,
    };
    try {
      const res = await GetAllPaymentPlans(body);
      res.result = formatPaymentPlansForTableFields(res.result);
      if (!(res && res.status && res.status !== 200)) {
        setPaymentPlansData({
          result: res.result,
          totalCount: res.totalCount,
        });
      }
    } catch (error) {
      console.error("error fetching payment plans:", error);
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("Something-went-wrong"),
        onclose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  }, [pagination, filter, filterWithOperator, propertyId]);

  const handleDeletePaymentPlan = async () => {
    try {
      const res = await DeletePaymentPlan(
        activeItem && activeItem.propertyPaymentPlanId
      );

      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("payment-plan-deleted-successfully"),
          onClose: () => setAlertBoxContent(null),
        });
        getAllPaymentPlans();
        onDelete();
      }
    } catch (error) {
      console.error("error deleting payment plan:", error);
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("Something-went-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
    }
  };

  const handleFilterConfirm = (params, title, inputValue, selectedValue) => {
    if (params.field === "PlanName") {
      setFilter((prev) => ({
        ...prev,
        paymentPlanName: inputValue,
        searchType: 1,
      }));
    } else if (
      params.field === "downPayment" ||
      params.field === "handoverPayment"
    ) {
      setFilterWithOperator((prev) => ({
        ...prev,
        [params.field]: {
          value: inputValue,
          operator: selectedValue,
        },
      }));
    } else if (params.field === "status") {
      setFilter((prev) => ({
        ...prev,
        propertyPlanStatus: selectedValue,
      }));
    }
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  };

  const onClearHandler = () => {
    setFilter(initialFilter);
    setFilterWithOperator(initialFilterWithOperator);
  };

  const restConfirmationState = () => {
    setActiveItem({});
    setIsOpenDeleteConfirmation(false);
  };

  const addPaymentPlanHandler = () => {
    setActiveItem({});
    setIsOpenAddPaymentPlanMenu(true);
  };

  useImperativeHandle(ref, () => ({
    addPaymentPlanHandler,
  }));

  useEffect(() => {
    if (propertyId) getAllPaymentPlans();
  }, [propertyId, getAllPaymentPlans]);

  return (
    <Box>
      <Typography className={styles.header}>
        {translate("predefined-payment-plans")}
      </Typography>
      {children}
      <Box className={styles.filterContainer}>
        <Typography className={styles.header}>
          {translate("selected-payment-plans")}
        </Typography>
        <CustomButton
          boxShadow="xs"
          size="md"
          style={{ marginBottom: "12px" }}
          variant="outlined"
          color="secondary"
          onClick={onClearHandler}
          startIcon={
            <FilterIcon
              width="20"
              height="20"
              fill={palette.button.secondary_fg}
            />
          }
        >
          {translate("clear-filters")}
        </CustomButton>
      </Box>
      {isLoading ? (
        <TableSkeleton rows={12} />
      ) : (
        <BasicTable
          rowHeight={84}
          setActiveItem={setActiveItem}
          setActiveItemOnRowClick={(item) => {
            setActiveItem(item);
          }}
          rowsData={
            paymentPlansData?.result.map((item) => {
              return {
                ...item,
                id: item.propertyPaymentPlanId,
              };
            }) || []
          }
          columns={PaymentPlanTableHeaderData(isDarkMode, handleFilterConfirm)}
          tableActions={({ anchorPosition }) => {
            if (!activeItem || Object.keys(activeItem).length === 0)
              return null;
            return (
              <TableEditDeleteActions
                anchorPosition={anchorPosition}
                onDelete={() => setIsOpenDeleteConfirmation(true)}
                onEdit={() => setIsOpenAddPaymentPlanMenu(true)}
              />
            );
          }}
          emptyState={{ NoRowsOverlay: () => <Box></Box> }}
        />
      )}
      <CustomPagination
        totalItems={paymentPlansData?.totalCount}
        itemsPerPage={pagination.pageSize}
        currentPage={pagination.pageIndex}
        inputValue={InputValue}
        setInputValue={setInputValue}
        onPageChange={(page) =>
          setPagination((prev) => ({ ...prev, pageIndex: page }))
        }
        onItemsPerPageChange={(items) =>
          setPagination((prev) => ({ ...prev, pageSize: items }))
        }
        isLoading={isLoading}
      />
      {Boolean(activeItem) && isOpenDeleteConfirmation && (
        <DeleteConfirmationDialog
          title={translate("payment-plan-will-be-deleted")}
          subTitle={translate(
            "are-you-sure-you-want-to-delete-this-payment-plan-this-action-cannot-be-undone"
          )}
          onClose={() => {
            restConfirmationState();
          }}
          onConfirm={() => {
            handleDeletePaymentPlan();
            setIsOpenDeleteConfirmation(false);
          }}
        />
      )}
      {IsOpenAddPaymentPlanMenu && (
        <PaymentPlanDialog
          onOpen={IsOpenAddPaymentPlanMenu}
          onClose={() => {
            setIsOpenAddPaymentPlanMenu(false);
            setActiveItem({});
          }}
          activeItem={activeItem}
          propertyId={propertyId}
          reloadData={() => getAllPaymentPlans()}
        />
      )}
    </Box>
  );
});

export default NewPaymentPlansView;
